import { useMemo, useState } from "react";
import { Card, Radio } from "antd";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import Footer from "../components/Footer";
import Header from "../components/Header";
import StandardButton from "../components/StandardButton";

const MODE_ENCODE = "encode";
const MODE_DECODE = "decode";
const VARIANT_STANDARD = "standard";
const VARIANT_URLSAFE = "urlsafe";

const encodeText = (text, variant) => {
  const bytes = new TextEncoder().encode(text);
  let binary = "";
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  const standard = btoa(binary);
  if (variant === VARIANT_URLSAFE) {
    return standard.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
  }
  return standard;
};

const decodeText = (input, variant) => {
  let normalized = input.replace(/\s+/g, "");
  if (variant === VARIANT_URLSAFE) {
    normalized = normalized.replace(/-/g, "+").replace(/_/g, "/");
  }
  const padNeeded = (4 - (normalized.length % 4)) % 4;
  normalized += "=".repeat(padNeeded);
  if (!/^[A-Za-z0-9+/]*={0,2}$/.test(normalized)) {
    throw new Error("invalid_chars");
  }
  const binary = atob(normalized);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new TextDecoder("utf-8", { fatal: true }).decode(bytes);
};

const DEFAULT_INPUT = "Quick Tools for Day-to-Day Developer Use";

const Base64Codec = () => {
  const { t } = useTranslation();
  const [mode, setMode] = useState(MODE_ENCODE);
  const [variant, setVariant] = useState(VARIANT_STANDARD);
  const [input, setInput] = useState(DEFAULT_INPUT);

  const { output, error } = useMemo(() => {
    if (input === "") return { output: "", error: null };
    try {
      const result =
        mode === MODE_ENCODE
          ? encodeText(input, variant)
          : decodeText(input, variant);
      return { output: result, error: null };
    } catch {
      return {
        output: "",
        error:
          mode === MODE_ENCODE
            ? "base64_codec_encode_error"
            : "base64_codec_decode_error",
      };
    }
  }, [input, mode, variant]);

  const inputByteLength = useMemo(
    () => new TextEncoder().encode(input).length,
    [input]
  );
  const outputByteLength = useMemo(
    () => new TextEncoder().encode(output).length,
    [output]
  );
  const sizeRatio =
    inputByteLength > 0 && outputByteLength > 0
      ? Math.round((outputByteLength / inputByteLength) * 100)
      : null;

  const swap = () => {
    if (!output || error) {
      toast.error(t("base64_codec_nothing_to_swap"));
      return;
    }
    setInput(output);
    setMode(mode === MODE_ENCODE ? MODE_DECODE : MODE_ENCODE);
  };

  const copy = async () => {
    if (!output) {
      toast.error(t("nothing_to_copy"));
      return;
    }
    try {
      await navigator.clipboard.writeText(output);
      toast.success(t("copy_success"));
    } catch {
      toast.error(t("failed_to_copy"));
    }
  };

  const clear = () => setInput("");

  const inputLabelKey =
    mode === MODE_ENCODE
      ? "base64_codec_input_text_label"
      : "base64_codec_input_b64_label";
  const inputPlaceholderKey =
    mode === MODE_ENCODE
      ? "base64_codec_input_text_placeholder"
      : "base64_codec_input_b64_placeholder";
  const outputLabelKey =
    mode === MODE_ENCODE
      ? "base64_codec_output_b64_label"
      : "base64_codec_output_text_label";

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-indigo-50 to-white p-0 text-gray-800">
      <Header />
      <div className="max-w-5xl mx-auto p-4">
        <Card className="shadow-lg rounded-xl">
          <div className="p-4 flex flex-col gap-6">
            <div>
              <h1 className="text-2xl font-bold text-indigo-700">
                {t("base64_codec_title")}
              </h1>
              <p className="text-gray-600 mt-1">{t("base64_codec_intro")}</p>
            </div>

            <div className="flex flex-wrap items-center gap-4">
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700">
                  {t("base64_codec_mode_label")}
                </label>
                <Radio.Group
                  value={mode}
                  onChange={(e) => setMode(e.target.value)}
                >
                  <Radio.Button value={MODE_ENCODE}>
                    {t("base64_codec_mode_encode")}
                  </Radio.Button>
                  <Radio.Button value={MODE_DECODE}>
                    {t("base64_codec_mode_decode")}
                  </Radio.Button>
                </Radio.Group>
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700">
                  {t("base64_codec_variant_label")}
                </label>
                <Radio.Group
                  value={variant}
                  onChange={(e) => setVariant(e.target.value)}
                >
                  <Radio.Button value={VARIANT_STANDARD}>
                    {t("base64_codec_variant_standard")}
                  </Radio.Button>
                  <Radio.Button value={VARIANT_URLSAFE}>
                    {t("base64_codec_variant_urlsafe")}
                  </Radio.Button>
                </Radio.Group>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">
                {t(inputLabelKey)}
              </label>
              <textarea
                rows={6}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-400 focus:outline-none font-mono text-sm"
                placeholder={t(inputPlaceholderKey)}
                value={input}
                onChange={(e) => setInput(e.target.value)}
              />
              <div className="flex flex-wrap gap-3 items-center">
                <StandardButton
                  onClick={swap}
                  type="secondary"
                  variant="outlined"
                  size="small"
                >
                  {t("base64_codec_swap")}
                </StandardButton>
                <StandardButton
                  onClick={clear}
                  type="secondary"
                  variant="outlined"
                  size="small"
                >
                  {t("base64_codec_clear")}
                </StandardButton>
                <span className="text-xs text-gray-500">
                  {t("base64_codec_input_byte_count", {
                    count: inputByteLength,
                  })}
                </span>
              </div>
            </div>

            {error && (
              <p className="text-sm text-red-600">{t(error)}</p>
            )}

            {!error && output && (
              <div className="bg-indigo-50 p-4 rounded-md border border-indigo-200">
                <div className="flex items-center justify-between gap-3">
                  <strong className="text-indigo-700">
                    {t(outputLabelKey)}
                  </strong>
                  <StandardButton
                    onClick={copy}
                    type="secondary"
                    variant="outlined"
                    size="small"
                  >
                    {t("copy_button")}
                  </StandardButton>
                </div>
                <p className="break-all mt-2 text-gray-800 font-mono text-sm whitespace-pre-wrap">
                  {output}
                </p>
                <p className="mt-2 text-xs text-indigo-700/80">
                  {t("base64_codec_output_byte_count", {
                    count: outputByteLength,
                  })}
                  {sizeRatio !== null && (
                    <>
                      {" · "}
                      {t("base64_codec_size_ratio", { percent: sizeRatio })}
                    </>
                  )}
                </p>
              </div>
            )}

            <div className="text-xs text-gray-500 leading-relaxed">
              <p>{t("base64_codec_note_unicode")}</p>
              <p>{t("base64_codec_note_urlsafe")}</p>
              <p>{t("base64_codec_note_privacy")}</p>
            </div>
          </div>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default Base64Codec;
